import { useEffect, useRef, useState } from 'react';

import { SubmitButton } from '@capsule/consumer-design-system';

import { useMediaQuery } from '@react-hookz/web';
import { Form, useFormikContext } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { css, useTheme } from 'styled-components';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { FormikPhoneField } from 'src/app/components/Form/FormikPhoneField/FormikPhoneField';
import { useAccount } from 'src/app/hooks/data/account';
import { usePharmacySearch } from 'src/app/hooks/data/pharmacy';
import { useUser } from 'src/app/hooks/data/user';
import { useZipCodeDeliverabilityCheck } from 'src/app/hooks/data/zipCode';
import { useGetStartedPersistentData } from 'src/app/hooks/feature_data/useGetStartedPersistentData';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import type { PlaceDetail } from 'src/app/types';
import { CAPSULE_ICON_STRINGS, FORM_INPUT_ICON_STRINGS } from 'src/consts';

import { SearchResultsView } from './SearchResultsView';
import { FauxLabel, FormikTextFieldWithTextareaStyles, TextFieldTransparentButton } from './styles';
import { sortSearchResultsByZip } from './utils';

const EMPTY_SPACE = ' ';

export enum visualizationToggleOptions {
  MAP = 'MAP',
  LIST = 'LIST',
}

export type PharmacyFormValues = {
  pharmacyName: string;
  pharmacyStreets: string;
  pharmacyPhone: string;
  pharmacyZipCode: string;
  pharmacyZipCodeLatitude?: string;
  pharmacyZipCodeLongitude?: string;
  isNewEntry: boolean;
};

export const PharmacyFormRenderer = ({
  initialZipCode,
  transferForm,
  setShowFooter,
  hasParentForm,
}: {
  initialZipCode: string;
  transferForm: any; // This can be a logged in or onboarding form. Typing as any for now
  setShowFooter: (showFooter: boolean) => void;
  hasParentForm: boolean;
}) => {
  /* Miscellaneous page layout related functionality and state */
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.smallDown);

  useEffect(() => {
    setShowFooter(!isMobile);
  }, [isMobile, setShowFooter]);

  const [isFullScreen, setFullScreen] = useState(false);
  const [isNewEntry, setIsNewEntry] = useState(transferForm && transferForm.isNewEntry);
  // The onboarding form state is an object with empty values when not in onboarding. Use "flow" as a proxy.
  const isOnboarding = useRef<boolean>(
    transferForm && transferForm?.onboardingForm && !!transferForm.onboardingForm.flow
  );

  const [nearbyPlaces, setNearbyPlaces] = useState<PlaceDetail[]>([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState<PlaceDetail | null>(null);
  const [visualization, setVisualization] = useState<visualizationToggleOptions>(visualizationToggleOptions.LIST);

  const {
    values,
    touched,
    setFieldValue,
    submitForm: handleSubmit,
    errors,
    isSubmitting,
    setValues,
    validateForm,
  } = useFormikContext<PharmacyFormValues>();

  useEffect(() => {
    if (!values.pharmacyName) {
      setIsNewEntry(false);
    }
  }, [values]);

  /* Functionality and state for the pharmacy search. We need a starting latitude/longitude + radius to frame
   * the search. To get there, we'll use a zip code either from:
   * 1. user's logged in account
   * 2. onboarding zip code from "new" GetStarted persistent data hook
   * 3. Redux store's initialZipCode
   *
   * We should remove the Redux store fallback from this code when we migrate to the "new" GetStarted
   * onboarding flow.
   */
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';
  const { data: pharmakonAccount } = useAccount(parseInt(accountId), { enabled: Boolean(accountId) });
  const { getStartedPersistentData } = useGetStartedPersistentData();
  const onboardingZipcode = getStartedPersistentData?.zipCode;
  // TODO: Remove this defaulting to initialZipCode from redux store here
  const [userZipCode, setUserZipCode] = useState(onboardingZipcode || initialZipCode);
  const [location, setLocation] = useState('');
  const [radius, setRadius] = useState(5000);

  const { data: isZipCodeDeliverable } = useZipCodeDeliverabilityCheck(userZipCode, { enabled: Boolean(userZipCode) });

  useEffect(() => {
    setUserZipCode(pharmakonAccount?.zipCode);
    if (isZipCodeDeliverable) {
      setLocation(`${isZipCodeDeliverable.latitude},${isZipCodeDeliverable.longitude}`);
    }
  }, [pharmakonAccount, isZipCodeDeliverable]);

  useEffect(() => {
    const location =
      values.pharmacyZipCodeLatitude && `${values.pharmacyZipCodeLatitude},${values.pharmacyZipCodeLongitude}`;
    if (location) {
      setLocation(location);
    }
  }, [values]);

  const textInputRef = useRef<HTMLInputElement | null>(null);
  const textFieldBackButtonRef = useRef<HTMLButtonElement | null>(null);
  const textFieldClearButtonRef = useRef<HTMLButtonElement | null>(null);

  // The pharmacy search hook is "lazy". It will only fetch when user input is updated, or when the refetch function is called explicitly
  const {
    data: pharmacyResults,
    refetch,
    isFetching,
  } = usePharmacySearch(textInputRef?.current?.value || '', location, radius);

  useEffect(() => {
    const fetchPharmacy = async () => {
      await refetch();
    };
    const query = textInputRef?.current?.value;
    if (!!query && query?.length > 0) {
      fetchPharmacy();
    }
  }, [textInputRef?.current?.value, refetch]);

  useEffect(() => {
    if (!pharmacyResults) return;
    const sortedResults = sortSearchResultsByZip(pharmacyResults?.data?.results, values.pharmacyZipCode || userZipCode);
    setNearbyPlaces(sortedResults);
  }, [pharmacyResults, userZipCode, values.pharmacyZipCode]);

  const handleSelectItem = async (selectedPharmacy: PlaceDetail) => {
    if (!selectedPharmacy.name) return;
    setIsNewEntry(false);
    setValues({
      ...values,
      pharmacyName: selectedPharmacy.name,
      pharmacyStreets: selectedPharmacy.formatted_address,
      pharmacyPhone: '',
      isNewEntry: false,
    });

    // Update query data with selected pharmacy physical location and name, so we show a map centered around it
    if (selectedPharmacy.geometry?.location) {
      setLocation(`${selectedPharmacy.geometry?.location?.lat},${selectedPharmacy.geometry?.location?.lng}`);
    }
    setSelectedPharmacy(selectedPharmacy);
    await refetch();
    if (textInputRef.current === null) return;
    textInputRef.current.blur();
  };

  /*
  We allow users to manually select a pharmacy name not returned by search results. This will require the user to manually add a phone number.
  */
  const handleSelectNonSearchItem = async (item: PlaceDetail) => {
    if (!item.name) return;
    setIsNewEntry(true);
    setValues({
      ...values,
      pharmacyName: item.name,
      pharmacyStreets: '',
      isNewEntry: true,
    });
    textInputRef?.current?.blur();
    validateForm();
  };

  const handleOnFocus = () => {
    if (isMobile) {
      setFullScreen(true);
    }
    setIsNewEntry(false);
    setVisualization(visualizationToggleOptions.LIST);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const fetchPharmacy = async () => {
      await refetch();
    };
    if (textInputRef.current === null) return;
    switch (e.key) {
      case 'Enter':
        fetchPharmacy();
        textInputRef.current.blur();
        break;
      case 'Escape':
        textInputRef.current.blur();
        break;
    }
  };

  const searchBoxLeftIcon =
    !isFullScreen && !(textInputRef.current === document.activeElement)
      ? FORM_INPUT_ICON_STRINGS['system_search']
      : FORM_INPUT_ICON_STRINGS['chevron_left'];
  useSegmentEvent('viewTransferPharmacyInfo', { shouldTrackOnMount: true, payload: { form_type: 'pharmacy form' } });

  const hasError =
    touched?.pharmacyName === true && !isEmpty(errors) && textInputRef.current !== document.activeElement;
  return (
    <Form
      onSubmit={handleSubmit}
      css={
        isFullScreen && isMobile && !(visualization === visualizationToggleOptions.MAP)
          ? /* The form is repositioned when on mobile + "full screen" mode in order to occupy
             * the whole viewport. Height is calculated based on the viewport height minus the header + progress bar
             * and footer height.
             * Also, the z-index manual override is there to make sure users can scroll through the results, since
             * the parent element has a "EmptyCol" element that kept blocking that.
             *
             * When a parent form ref is sent in, override this and let the parent element deal with this stuff. This
             * component was originally built for inclusion in transfer forms, but we can use it elsewhere this  way. We
             * should devote some time to try to make this a little more elegant in the future if refactoring the transfer forms.
             */
            css`
              position: relative;
              top: ${isOnboarding.current ? '-120px' : `-178px`};
              margin-bottom: -178px;
              left: -${({ theme }) => theme.space.s5};
              width: 100vw !important;
              background: white;
              height: calc(100vh - 90px);
              padding: 0 ${({ theme }) => theme.space.s5};
              z-index: ${({ theme }) => theme.zIndex.z1};
            `
          : // When not in mobile "full screen" mode, we are just dragging the form up to take the place of a <h2>
            // element that belongs to the parent component and should be omitted in this layout. When we refactor
            // forms to not use the "STEP" construct, we can remove this and conditionally render the <h2> element
            css`
              position: relative;
              z-index: ${({ theme }) => theme.zIndex.z1};
            `
      }
    >
      <FauxLabel variant="overline" hasError={hasError} isFocused={textInputRef.current === document.activeElement}>
        Pharmacy name, address, or zip
      </FauxLabel>
      <FormikTextFieldWithTextareaStyles
        name="pharmacyName"
        onBlur={e => {
          // Duplicates logic in the actual buttons because the TextInput intercepts clicks on blur.
          if (e.relatedTarget === textFieldBackButtonRef.current) {
            setFullScreen(false);
          } else if (e.relatedTarget === textFieldClearButtonRef.current && !!textInputRef.current) {
            setFieldValue('pharmacyName', '');
          }
        }}
        ref={textInputRef}
        label={{
          content: 'Pharmacy name, address, or zip',
          isHidden: true,
        }}
        adornment={
          isMobile && {
            left: (
              <>
                <TextFieldTransparentButton
                  type="button"
                  onClick={() => {
                    if (isFullScreen) {
                      setFullScreen(false);
                    }
                    setVisualization(visualizationToggleOptions.LIST);
                  }}
                  ref={textFieldBackButtonRef}
                />
                <CapsuleIcon
                  iconString={searchBoxLeftIcon}
                  css={css`
                    padding: 0px !important;
                    height: 36px !important;
                    width: 36px !important;
                    svg {
                      height: 36px !important;
                      width: 36px !important;
                    }
                    margin-bottom: ${({ theme }) => theme.space.s3};
                  `}
                />
              </>
            ),
            right: (
              <>
                <TextFieldTransparentButton
                  type="button"
                  onClick={() => {
                    setFieldValue('pharmacyName', '');
                  }}
                  ref={textFieldClearButtonRef}
                />
                <CapsuleIcon
                  iconString={CAPSULE_ICON_STRINGS.close}
                  css={css`
                    padding: 0px !important;
                    height: 36px !important;
                    width: 36px !important;
                    svg {
                      height: 36px !important;
                      width: 36px !important;
                    }
                    margin-bottom: ${({ theme }) => theme.space.s3};
                  `}
                />
              </>
            ),
          }
        }
        placeholder={
          isFullScreen || (!isMobile && textInputRef.current === document.activeElement)
            ? 'Pharmacy name, address, or zip'
            : ''
        }
        onFocus={handleOnFocus}
        description={
          !(textInputRef.current === document.activeElement) && !selectedPharmacy
            ? 'For example: “CVS 86th St”'
            : EMPTY_SPACE
        }
        onKeyDown={e => {
          handleKeyDown(e);
        }}
        autoComplete="off"
        type="text"
      />

      <SearchResultsView
        isFetchingNearby={isFetching}
        nearbyPlaces={nearbyPlaces}
        selectedPharmacy={selectedPharmacy}
        isNewEntry={isNewEntry}
        selectItem={handleSelectItem}
        selectNonSearchItem={handleSelectNonSearchItem}
        searchProps={{ setLocation, setRadius, refetch }}
        textInputRef={textInputRef}
        displayProps={{ visualization, setVisualization, isFullScreen, hasParentForm }}
      />

      <FormikPhoneField
        name="pharmacyPhone"
        label={{
          content: `Phone number`,
          isHidden: !isNewEntry,
        }}
        css={css`
          display: ${isNewEntry ? 'block' : 'none'};
        `}
      />

      {!hasParentForm && (
        <SubmitButton
          variant="contained-secondary-lg"
          isSubmitting={isSubmitting}
          css={
            isMobile
              ? css`
                  position: fixed;
                  bottom: 0;
                  width: ${({ theme }) => `calc(100% - ${theme.space.s5} - ${theme.space.s5})`};
                  margin: 0 auto ${({ theme }) => theme.space.s5};
                `
              : css`
                  position: relative;
                  max-width: 24rem;
                  margin-top: ${({ theme }) => theme.space.s4};
                `
          }
          isFullWidth
        >
          Continue
        </SubmitButton>
      )}
    </Form>
  );
};
